import { useState } from "react";
import type { ImageDTO } from "@shared/types";
import { formatBytes, mimeLabel } from "../lib/format";
import { CopyUrlButton } from "./CopyUrlButton";

interface Props {
  image: ImageDTO;
  showPendingBadge?: boolean;
  onPreview: () => void;
  onRename: () => void;
  onMove: () => void;
  onDelete: () => void;
  onDetails: () => void;
}

export function ImageCard({
  image,
  showPendingBadge,
  onPreview,
  onRename,
  onMove,
  onDelete,
  onDetails,
}: Props) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [broken, setBroken] = useState(false);

  const menuItem = (label: string, fn: () => void, danger = false) => (
    <button
      onClick={() => {
        setMenuOpen(false);
        fn();
      }}
      className={`block w-full px-3 py-1.5 text-left text-[13px] transition-colors hover:bg-white/70 ${
        danger ? "text-red-600" : "text-zinc-700"
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="glass group relative flex flex-col overflow-hidden rounded-xl transition-all duration-150 hover:bg-white/78 hover:shadow-glass-lg">
      <button
        onClick={onPreview}
        className="checkerboard relative block aspect-square w-full overflow-hidden border-b border-white/60"
      >
        {broken ? (
          <span className="grid h-full w-full place-items-center text-2xs text-zinc-400">preview unavailable</span>
        ) : (
          <img
            src={image.url}
            alt={image.filename}
            loading="lazy"
            onError={() => setBroken(true)}
            className="h-full w-full object-contain"
          />
        )}
        {showPendingBadge && image.status === "pending" && (
          <span className="glass glass-strong absolute left-2 top-2 flex items-center gap-1.5 rounded-lg px-2 py-1 text-2xs font-medium text-zinc-700">
            <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
            Publishing
          </span>
        )}
      </button>

      <div className="flex min-w-0 flex-1 flex-col gap-0.5 p-2.5 sm:p-3">
        <p className="truncate text-[13px] font-medium text-zinc-900" title={image.filename}>
          {image.filename}
        </p>
        <p className="truncate text-2xs text-zinc-500">
          {formatBytes(image.sizeBytes)} · {mimeLabel(image.mimeType)}
          {image.width && image.height ? ` · ${image.width}×${image.height}` : ""}
        </p>

        <div className="mt-2.5 flex items-center gap-1.5">
          <CopyUrlButton url={image.url} compact className="min-w-0 flex-1 sm:flex-none" />
          <div className="relative ml-auto">
            <button
              onClick={() => setMenuOpen((o) => !o)}
              className="grid h-9 w-9 flex-shrink-0 place-items-center rounded-lg text-zinc-400 transition-colors hover:bg-white/80 hover:text-zinc-800"
              aria-label="More actions"
            >
              <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path d="M10 6a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Zm0 5.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Zm0 5.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Z" />
              </svg>
            </button>
            {menuOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
                <div className="glass glass-strong absolute right-0 z-20 mt-1.5 w-36 overflow-hidden rounded-xl py-1 shadow-glass-lg animate-scale-in">
                  {menuItem("Preview", onPreview)}
                  {menuItem("Details", onDetails)}
                  {menuItem("Rename", onRename)}
                  {menuItem("Move", onMove)}
                  <a
                    href={image.url}
                    download={image.filename}
                    onClick={() => setMenuOpen(false)}
                    className="block px-3 py-1.5 text-left text-[13px] text-zinc-700 transition-colors hover:bg-white/70"
                  >
                    Download
                  </a>
                  <div className="my-1 border-t border-white/70" />
                  {menuItem("Delete", onDelete, true)}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
